import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// The mail-list columns the user can show/hide and reorder. `name` (Nom / Objet)
// and the trailing actions column are fixed anchors and are NOT part of this set:
// name is always first, actions always last.
export type MailColumnId = 'from' | 'date' | 'sentAt' | 'size' | 'filedBy' | 'filedAt';

export const MAIL_COLUMN_META: Record<
  MailColumnId,
  { label: string; minWidth: number; defaultWidth: number }
> = {
  from: { label: 'De', minWidth: 90, defaultWidth: 150 },
  // « Reçu le » et non « Date » : la colonne porte `received_at` (avec repli sur
  // la date d'envoi quand la réception est inconnue). Face à la colonne
  // « Envoyé le », un intitulé « Date » ne disait pas laquelle des deux dates on
  // regardait — et les deux ne diffèrent que de quelques secondes, donc rien à
  // l'écran ne permettait de trancher. L'id reste `date` : c'est une clé
  // persistée dans les préférences de colonnes des utilisateurs (localStorage),
  // la renommer réinitialiserait leur mise en page.
  date: { label: 'Reçu le', minWidth: 90, defaultWidth: 120 },
  sentAt: { label: 'Envoyé le', minWidth: 90, defaultWidth: 120 },
  size: { label: 'Taille', minWidth: 64, defaultWidth: 90 },
  filedBy: { label: 'Rangé par', minWidth: 90, defaultWidth: 130 },
  filedAt: { label: 'Rangé le', minWidth: 90, defaultWidth: 120 },
};

export const ALL_MAIL_COLUMNS: MailColumnId[] = ['from', 'date', 'sentAt', 'size', 'filedBy', 'filedAt'];

// Jeu par défaut voulu : « Nom / Objet » (ancre, toujours première), « De » et
// « Envoyé le ». Tout le reste (Reçu le, Taille, Rangé par, Rangé le) reste
// disponible dans le sélecteur de colonnes, mais masqué tant qu'on ne l'active
// pas — un volet Outlook est étroit, et six colonnes y tronquaient l'objet du
// mail, qui est l'information la plus utile.
const DEFAULT_ORDER: MailColumnId[] = ['from', 'sentAt', 'date', 'size', 'filedBy', 'filedAt'];
const DEFAULT_HIDDEN: MailColumnId[] = ['date', 'size', 'filedBy', 'filedAt'];

function isMailColumnId(x: unknown): x is MailColumnId {
  return typeof x === 'string' && (ALL_MAIL_COLUMNS as string[]).includes(x);
}

// Reconcile a persisted order against the current column set: keep the stored
// order for known ids, drop unknown ones, and append any newly-added columns at
// the end. Keeps old localStorage values working when the column set evolves.
function reconcileOrder(order: unknown): MailColumnId[] {
  const kept = Array.isArray(order) ? order.filter(isMailColumnId) : [];
  const seen = new Set(kept);
  return [...kept, ...ALL_MAIL_COLUMNS.filter((id) => !seen.has(id))];
}

function reconcileHidden(hidden: unknown): MailColumnId[] {
  return Array.isArray(hidden) ? hidden.filter(isMailColumnId) : [];
}

interface MailColumnPrefsState {
  /** All toggleable columns, in display order. */
  order: MailColumnId[];
  /** Subset currently hidden. */
  hidden: MailColumnId[];
  toggle: (id: MailColumnId) => void;
  /** Move `activeId` to `overId`'s position (arrayMove semantics). */
  reorder: (activeId: MailColumnId, overId: MailColumnId) => void;
  reset: () => void;
}

export const useMailColumnPrefs = create<MailColumnPrefsState>()(
  persist(
    (set) => ({
      order: DEFAULT_ORDER,
      hidden: DEFAULT_HIDDEN,
      toggle: (id) =>
        set((s) => ({
          hidden: s.hidden.includes(id) ? s.hidden.filter((x) => x !== id) : [...s.hidden, id],
        })),
      reorder: (activeId, overId) =>
        set((s) => {
          const from = s.order.indexOf(activeId);
          const to = s.order.indexOf(overId);
          if (from < 0 || to < 0 || from === to) return s;
          const next = [...s.order];
          next.splice(from, 1); // remove activeId…
          next.splice(to, 0, activeId); // …and re-insert it at overId's slot
          return { order: next };
        }),
      reset: () => set({ order: DEFAULT_ORDER, hidden: DEFAULT_HIDDEN }),
    }),
    {
      name: 'prism-mail-columns',
      // 1 → 2 : nouveau jeu par défaut (De + Envoyé le). Sans ce bump, le
      // changement n'aurait aucun effet sur les utilisateurs EXISTANTS — leur
      // `hidden` est déjà dans le localStorage, et `merge` le respecterait. Or la
      // consigne est un défaut « pour tous les utilisateurs ».
      version: 2,
      // Contrepartie assumée : une disposition de colonnes personnalisée est
      // réinitialisée UNE fois. C'est une préférence d'affichage, retrouvable en
      // deux clics dans le sélecteur de colonnes ; la garder aurait laissé
      // l'ancien jeu en place chez précisément ceux qui l'avaient réglé.
      migrate: () => ({ order: DEFAULT_ORDER, hidden: DEFAULT_HIDDEN }),
      // Normalize whatever was persisted so a stale/partial value never breaks
      // the grid (missing new columns, unknown ids, wrong shape). On a first
      // load there is nothing persisted — merge still runs with an empty value,
      // so we MUST return `current` (the defaults) untouched; reconciling from
      // nothing would otherwise show every column instead of the default set.
      merge: (persisted, current) => {
        if (!persisted || typeof persisted !== 'object') return current;
        const p = persisted as Partial<MailColumnPrefsState>;
        return { ...current, order: reconcileOrder(p.order), hidden: reconcileHidden(p.hidden) };
      },
    },
  ),
);

// Ré-exporté depuis `utils/fileSize` : la fonction a été déplacée quand l'onglet
// d'administration en a eu besoin (voir le commentaire de ce module). Les points
// d'appel existants continuent de l'importer d'ici.
export { humanFileSize } from '@/utils/fileSize';
